/**
 * settings/cards/LegalComplianceCard.tsx
 * --------------------------------------
 * What: Entry points for GDPR SAR, document acceptance, and related flows.
 * Why: Groups legal compliance tools in one obvious place for users.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  ActivityIndicator,
  Alert,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../lib/AuthContext';
import { useTheme } from '../../../../lib/ThemeContext';
import { useSubscription } from '../../../../lib/SubscriptionContext';
import { useLegal } from '../../../../lib/LegalContext';
import DocumentViewerModal from '../../../components/DocumentViewerModal';
import ThemedButton from '../../../components/ThemedButton';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function LegalComplianceCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();
  const { user } = useAuth();
  const { dateFormat } = useSubscription();
  const { acceptedDocuments } = useLegal();

  /* ─────────── State ─────────── */
  const [currentIp, setCurrentIp] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<string | null>(null);

  // SAR state
  const [showSarOtpModal, setShowSarOtpModal] = useState(false);
  const [sarOtp, setSarOtp] = useState('');
  const [sarOtpLoading, setSarOtpLoading] = useState(false);
  const [sarOtpError, setSarOtpError] = useState('');
  const [showSarCooldownModal, setShowSarCooldownModal] = useState(false);
  const [sarCooldownMessage, setSarCooldownMessage] = useState('');
  const [showSarSuccessModal, setShowSarSuccessModal] = useState(false);
  const [sarInitLoading, setSarInitLoading] = useState(false);
  const [sarLoadingMessage, setSarLoadingMessage] = useState('');
  const [sarResendLoading, setSarResendLoading] = useState(false);

  /* ─────────── Helpers ─────────── */
  const labelForDocType = useCallback((type: string) => {
    switch (type) {
      case 'terms':
        return 'Terms & Conditions';
      case 'privacy':
        return 'Privacy Policy';
      case 'gdpr_special':
        return 'GDPR Special Category Data Statement';
      default:
        return type;
    }
  }, []);

  const formatDate = useCallback(
    (iso: string) => {
      const d = new Date(iso);
      if (dateFormat === 'MM/DD/YYYY') return d.toLocaleDateString('en-US');
      if (dateFormat === 'YYYY-MM-DD') return d.toISOString().split('T')[0];
      return d.toLocaleDateString('en-GB');
    },
    [dateFormat]
  );

  // Only keep the latest accepted version of each type
  const latestAcceptedDocuments = useMemo(() => {
    const byType = new Map<string, typeof acceptedDocuments[number]>();
    acceptedDocuments.forEach((doc) => {
      const existing = byType.get(doc.doc_type);
      if (!existing || new Date(doc.accepted_at) > new Date(existing.accepted_at)) {
        byType.set(doc.doc_type, doc);
      }
    });
    return Array.from(byType.values()).sort((a, b) => a.doc_type.localeCompare(b.doc_type));
  }, [acceptedDocuments]);

  // Fetch IP once when expanded
  useEffect(() => {
    if (expanded && currentIp === null) {
      const getIp = async () => {
        try {
          const resp = await fetch('https://api.ipify.org?format=json');
          const json = await resp.json();
          setCurrentIp(json.ip);
        } catch {}
      };
      getIp();
    }
  }, [expanded, currentIp]);

  /* ─────────── SAR Handlers ─────────── */
  const handleSarPress = async () => {
    if (!user) return;
    setShowSarOtpModal(false);
    setSarLoadingMessage('Checking eligibility…');
    setSarInitLoading(true);
    try {
      const { data: eligibility, error: eligError } = await supabase.functions.invoke('check-sar-eligibility', {
        body: { userId: user.id },
      });
      if (eligError) throw eligError;
      if (!eligibility?.eligible) {
        const providedDate = eligibility?.last_provided_at ? new Date(eligibility.last_provided_at).toLocaleDateString() : 'unknown date';
        const nextDate = eligibility?.next_allowed_at ? new Date(eligibility.next_allowed_at).toLocaleDateString() : 'unknown date';
        setSarCooldownMessage(
          `You already requested this data on ${providedDate}. Another request now would be considered a repeated request and excessive under GDPR Article 12(5).\n\n` +
          `You can next request a SAR report on ${nextDate}.\n\n` +
          `If you have a genuine need for another report before this date, please use the "Contact the Developers" area to request this.\n\n` +
          `We reserve the right to charge a reasonable fee for processing excessive requests which will be explained at the time of your contact.\n\n` +
          `You have the right to complain to a supervisory authority if you do not agree with the charge for excessive requests.`
        );
        setSarInitLoading(false);
        setShowSarCooldownModal(true);
      } else {
        setSarLoadingMessage('Sending verification code…');
        await supabase.functions.invoke('send-sar-otp', { body: { userId: user.id, email: user.email } });
        setSarOtpError('');
        setSarInitLoading(false);
        setShowSarOtpModal(true);
      }
    } catch (err: any) {
      setSarInitLoading(false);
      Alert.alert('Error', err?.message || 'Failed to initiate SAR request');
    }
  };

  const handleSarVerify = async () => {
    if (!user) return;
    if (!sarOtp) {
      Alert.alert('Error', 'Please enter the verification code');
      return;
    }
    setSarOtpLoading(true);
    setSarOtpError('');
    try {
      const { data: verifyRes, error: verifyErr } = await supabase.functions.invoke('verify-sar-otp', {
        body: { userId: user.id, otp: sarOtp },
      });
      if (verifyErr || verifyRes?.success === false) {
        setSarOtpError('Incorrect or expired code. Please try again.');
        return;
      }
      if (verifyRes?.rateLimited) {
        setShowSarOtpModal(false);
        setSarOtp('');
        setSarOtpError('');
        setSarCooldownMessage('You have already requested a SAR in the last 30 days.');
        setShowSarCooldownModal(true);
        return;
      }
      setShowSarOtpModal(false);
      setSarOtp('');
      setSarOtpError('');
      setShowSarSuccessModal(true);
    } catch (err: any) {
      setSarOtpError('Verification failed. Please try again.');
    } finally {
      setSarOtpLoading(false);
    }
  };

  const handleSarResend = async () => {
    if (!user) return;
    setSarResendLoading(true);
    try {
      await supabase.functions.invoke('send-sar-otp', { body: { userId: user.id, email: user.email } });
      Alert.alert('New Code Sent', 'We have emailed you a new verification code.');
    } catch {
      setSarOtpError('Failed to resend code. Please try again later.');
    } finally {
      setSarResendLoading(false);
    }
  };

  /* ─────────── Styles ─────────── */
  const glowStyle = useMemo(
    () =>
      themeType === 'blueNeon'
        ? Platform.OS === 'web'
          ? ({ boxShadow: `0 0 6px 1px ${theme.accent}` } as any)
          : {
              shadowColor: theme.accent,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 6,
              elevation: 6,
            }
        : {},
    [themeType, theme.accent]
  );

  const cardStyle = [styles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  /* ─────────── Render ─────────── */
  return (
    <>
      <View style={cardStyle}>
        <TouchableOpacity style={[styles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
          <Text style={[styles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Legal, Compliance & GDPR</Text>
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
        </TouchableOpacity>

        {expanded && (
          <>
            <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />
            {latestAcceptedDocuments.length ? (
              <>
                <Text style={[styles.detailsLabel, { color: theme.text, marginBottom: 8, fontSize: 14 }]}>Most recent accepted documents:</Text>
                {latestAcceptedDocuments.map((d, idx) => (
                  <View
                    key={d.id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: idx === latestAcceptedDocuments.length - 1 ? 16 : 8,
                    }}
                  >
                    <Text style={[styles.detailsValue, { color: theme.text, flex: 1, fontSize: 14 }]}>{labelForDocType(d.doc_type)} v{d.version}</Text>
                    <Text style={[styles.detailsValue, { color: theme.secondaryText, marginRight: 8, fontSize: 14 }]}>{formatDate(d.accepted_at)}</Text>
                    <TouchableOpacity onPress={() => setOpenDoc(d.url)}>
                      <Ionicons name="open-outline" size={20} color={theme.accent} />
                    </TouchableOpacity>
                  </View>
                ))}

                <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />

                <Text style={[styles.detailsLabel, { color: theme.text, marginTop: 0, marginBottom: 8, fontSize: 15 }]}>GDPR</Text>
                <TouchableOpacity
                  onPress={handleSarPress}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme.accent,
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 20,
                    alignSelf: 'flex-start',
                    marginTop: 8,
                    marginBottom: 16,
                  }}
                  activeOpacity={0.85}
                >
                  <Ionicons name="shield-checkmark-outline" size={18} color={theme.buttonText} style={{ marginRight: 8 }} />
                  <Text style={[styles.modalButtonText, { color: theme.buttonText, fontSize: 15 }]}>Request GDPR SAR</Text>
                </TouchableOpacity>

                <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />

                {currentIp && (
                  <Text style={[styles.detailsValue, { color: theme.secondaryText, fontSize: 14 }]}>Your current IP address: {currentIp}</Text>
                )}
              </>
            ) : (
              <Text style={[styles.detailsValue, { color: theme.text }]}>No acceptance records found.</Text>
            )}
          </>
        )}
      </View>

      {/* ───── SAR Success Modal ───── */}
      <Modal visible={showSarSuccessModal} transparent animationType="fade" onRequestClose={() => setShowSarSuccessModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Request Received</Text>
            <Text style={[styles.modalMessage, { color: theme.text }]}>We are preparing your SAR report. You will receive an email when it is ready to download.</Text>
            <ThemedButton title="OK" onPress={() => setShowSarSuccessModal(false)} style={{ marginTop: 8 }} />
          </View>
        </View>
      </Modal>

      {/* ───── SAR Loading Modal ───── */}
      <Modal transparent visible={sarInitLoading} animationType="fade" onRequestClose={() => {}}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}> 
            <ActivityIndicator size="large" color={theme.text} />
            <Text style={[styles.modalMessage, { marginTop: 16, color: theme.text }]}>{sarLoadingMessage}</Text>
          </View>
        </View>
      </Modal>

      {/* ───── SAR OTP Modal ───── */}
      <Modal visible={showSarOtpModal} transparent animationType="fade" onRequestClose={() => setShowSarOtpModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card, paddingVertical: 32 }]}> 
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Enter Verification Code</Text>
            <Text style={[styles.modalMessage, { color: theme.text, marginBottom: 20 }]}>We emailed you a 6-digit code. Please enter it below to continue.</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text, textAlign: 'center', letterSpacing: 4 }]}
              value={sarOtp}
              onChangeText={setSarOtp}
              onFocus={() => {
                if (sarOtpError) {
                  setSarOtp('');
                  setSarOtpError('');
                }
              }}
              onSubmitEditing={() => {
                if (!sarOtpLoading) {
                  handleSarVerify();
                }
              }}
              returnKeyType="done"
              blurOnSubmit={false}
              keyboardType="number-pad"
              maxLength={6}
              placeholder="••••••"
              placeholderTextColor={theme.secondaryText}
            />
            {sarOtpLoading && <ActivityIndicator style={{ marginTop: 16 }} color={theme.accent} />}
            {sarOtpError ? <Text style={[styles.modalMessage, { color: theme.error, marginTop: 12, textAlign: 'center' }]}>{sarOtpError}</Text> : null}
            {/expired/i.test(sarOtpError) && (
              <ThemedButton title={sarResendLoading ? 'Resending…' : 'Request New Code'} onPress={handleSarResend} disabled={sarResendLoading} style={{ marginTop: 12 }} />
            )}
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 24, width: '100%' }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => { setShowSarOtpModal(false); setSarOtp(''); }} style={{ flex: 1 }} />
              <ThemedButton title="Verify" onPress={handleSarVerify} style={{ flex: 1 }} disabled={sarOtpLoading} />
            </View>
          </View>
        </View>
      </Modal>

      {/* ───── SAR Cooldown Modal ───── */}
      <Modal transparent visible={showSarCooldownModal} animationType="fade" onRequestClose={() => setShowSarCooldownModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}> 
            <Text style={[styles.modalMessage, { color: theme.text }]}>{sarCooldownMessage}</Text>
            <ThemedButton title="OK" onPress={() => setShowSarCooldownModal(false)} style={{ marginTop: 16, minWidth: 140, alignSelf: 'center' }} />
          </View>
        </View>
      </Modal>

      {/* ───── Document Viewer ───── */}
      <DocumentViewerModal visible={!!openDoc} url={openDoc} onClose={() => setOpenDoc(null)} />
    </>
  );
}

const styles = StyleSheet.create({
  /* Card */
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
  },
  detailsLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  detailsValue: {
    fontSize: 16,
  },
  /* Modals */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#2a2a2a',
    padding: 24,
    borderRadius: 12,
    width: '85%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  modalButtonText: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
  },
  /* Inputs */
  input: {
    backgroundColor: '#222',
    borderWidth: 1,
    borderColor: '#333',
    padding: 15,
    borderRadius: 6,
    fontSize: 16,
    width: '100%',
  },
}); 